<template>
    <div class="card shadow" data-view="countries-table">
        <div class="card-header border-0">
            <div class="row align-items-center">
                <div class="col">
                    <h3 class="mb-0">
                        {{title}}
                    </h3>
                </div>
                <div class="col text-right">
                    <language-menu @change="reload" />
                    <base-button type="primary" size="sm" @click="createEntry">Neuer Eintrag</base-button>
                    <base-button v-if="!pagination.showAll" type="primary" size="sm" @click="showAll">Alle anzeigen</base-button>
                    <base-button type="primary" size="sm" @click="reload">Aktualisieren</base-button>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <base-table class="table align-items-center table-flush"
                    thead-classes="thead-light"
                    tbody-classes="list"
                    :data="tableData"
                    :column-count="3"
                    :show-spinner="spinners.table">
                <template slot="columns">
                    <th data-column="name">
                        <sorting-label title="Name" field="name" v-model="sorting" />
                        <base-input placeholder="Name suchen" type="search" inputClasses="form-control-sm" :noMargin="true" v-model="search.name" @input="searchData"></base-input>
                    </th>
                    <th data-column="short_code">
                        <sorting-label title="Abkürzung" field="short_code" v-model="sorting" />
                        <base-input placeholder="Abkürzung suchen" type="search" inputClasses="form-control-sm" :noMargin="true" v-model="search.short_code" @input="searchData"></base-input>
                    </th>
                    <th></th>
                </template>

                <template slot-scope="{row}">
                    <td data-column="name">
                        {{ row.name }}
                    </td>

                    <td data-column="short_code">
                        {{ row.short_code }}
                    </td>

                    <td class="d-flex justify-content-end align-items-center">
                        <i v-if="!row.translationStatus.translated" class="fas fa-language fa-2x mr-2" title="Noch nicht übersetzt"></i>

                        <base-button size="sm" @click="editSelectedEntry(row)">
                            <i class="fas fa-pen"></i>
                        </base-button>
                        
                        <base-button type="danger" size="sm" @click="showDeleteEntryModal(row)">
                            <i class="fas fa-trash"></i>
                        </base-button>
                    </td>
                </template>
            </base-table>
        </div>

        <div class="card-footer d-flex justify-content-end">
            <base-pagination v-if="tableData.length && !pagination.showAll" :total="pagination.total" :perPage="pagination.perPage" :value="pagination.value" @input="loadTableData" />
        </div>

        <modal :show.sync="deleteEntry.showModal"
            gradient="danger"
            modal-classes="modal-danger modal-dialog-centered">
            <div class="py-3 text-center">
                <i class="fas fa-trash fa-3x"></i>
                <h4 class="heading mt-4">Eintrag "{{ deleteEntry.title }}" wirklich löschen?</h4>
                <p class="mb-0">Das Löschen des Eintrags kann nicht rückgängig gemacht werden.</p>
            </div>

            <template slot="footer">
                <base-button type="white" @click="deleteSelectedEntry(deleteEntry.id)">Eintrag löschen</base-button>
                <base-button type="link"
                             text-color="white"
                             class="ml-auto"
                             @click="deleteEntry.showModal = false">
                    Abbrechen
                </base-button>
            </template>
        </modal>

        <modal :show.sync="editEntry.showModal"
               modal-classes="modal-dialog-centered">
            <div class="py-3">
                <h4 class="heading text-center">{{ editEntry.entry.id ? 'Land bearbeiten' : 'Neues Land anlegen' }}</h4>
                
                <form role="form" v-if="editEntry.showModal">
                    <div class="row pt-4">
                        <div class="col-lg-12">
                            <base-input label="Name"
                                        placeholder="Name"
                                        input-classes="form-control-alternative"
                                        :required="true"
                                        v-model="editEntry.entry.name"
                                        :valid="editEntry.showErrors ? !!editEntry.entry.name : null"
                                        :needs-translation="editEntry.entry.translationStatus.needsTranslation('name')"
                            />
                            <div class="text-danger invalid-feedback mt--2 mb-3 d-block" v-if="editEntry.showErrors && !editEntry.entry.name">Bitte geben Sie einen Namen an.</div>
                        </div>
                        <div class="col-lg-12">
                            <base-input label="Abkürzung (ISO 3166 ALPHA-2)"
                                        placeholder="Abkürzung"
                                        input-classes="form-control-alternative text-uppercase"
                                        :required="true"
                                        v-model="editEntry.entry.short_code"
                                        :valid="editEntry.showErrors ? (!!editEntry.entry.short_code && editEntry.entry.short_code.length === 2) : null"
                            />
                            <div class="text-danger invalid-feedback mt--2 mb-3 d-block" v-if="editEntry.showErrors && !editEntry.entry.short_code">Bitte geben Sie eine Abkürzung an (<a href="https://de.wikipedia.org/wiki/ISO-3166-1-Kodierliste">ISO 3166 ALPHA-2</a>).</div>
                            <div class="text-danger invalid-feedback mt--2 mb-3 d-block" v-else-if="editEntry.showErrors && editEntry.entry.short_code.length !== 2">Die Abkürzung muss genau 2 Zeichen lang sein (<a href="https://de.wikipedia.org/wiki/ISO-3166-1-Kodierliste">ISO 3166 ALPHA-2</a>).</div>
                        </div>
                    </div>
                </form>
            </div>

            <template slot="footer">
                <base-button type="primary" @click="saveEntry(editEntry.entry)">Speichern</base-button>
                <base-button type="link"
                             text-color="primary"
                             class="ml-auto"
                             @click="editEntry.showModal = false">
                    Schließen
                </base-button>
            </template>
        </modal>
    </div>
</template>
<script>
import apiService from '@/apiService'
import userSettings from '@/userSettings'
import Localized from '@/Localized'
import LanguageMenu from '@/views/Dashboard/LanguageMenu.vue'
import SortingLabel from '@/components/Table/SortingLabel.vue'

export default {
    name: 'countries-table',
    components: {
        LanguageMenu,
        SortingLabel,
    },
    props: {
        title: String,
    },
    data() {
        return {
            spinners: {
                table: false,
            },
            deleteEntry: {
                showModal: false,
                title: '',
                id: 0,
            },
            editEntry: {
                showModal: false,
                showErrors: false,
                entry: {},
            },
            tableData: [],
            table: 'country',
            pagination: {
                showAll: false,
                total: 0,
                perPage: userSettings.getSetting('rowsPerTable'),
                value: 1,
            },
            search: {
                name: '',
                short_code: '',
            },
            sorting: {
                field: 'name',
                dir: 'asc',
            },
        }
    },
    watch: {
        sorting() {
            this.reload();
        },
    },
    methods: {
        reload() {
            if(this.pagination.showAll) {
                this.showAll();
            } else {
                this.loadTableData();
            }
        },
        async showAll() {
            this.tableData = [];
            this.spinners.table = true;

            const response = await apiService.loadTableData(this.table, this.search, 0, this.sorting);

            if(response !== null) {
                this.tableData = response.data;
                this.pagination.showAll = true;
            }
            
            this.spinners.table = false;
        },
        showDeleteEntryModal(entry) {
            this.deleteEntry = {
                showModal: true,
                title: entry.name,
                id: entry.id,
            };
        },
        async deleteSelectedEntry(id) {
            const deleted = await apiService.deleteEntry(this.table, id);

            if(!deleted) {
                this.$notify({
                    type: 'danger',
                    title: 'Löschung fehlgeschlagen!'
                });
            } else {
                this.$notify({
                    type: 'success',
                    title: 'Eintrag erfolgreich gelöscht!'
                });

                this.deleteEntry = {
                    showModal: false,
                    title: '',
                    id: 0,
                };

                this.loadTableData();
            }
        },
        async loadTableData(page = null) {
            if(this.pagination.showAll) {
                return this.showAll();
            }
            
            this.tableData = [];
            this.spinners.table = true;

            const pageToLoad = page || this.pagination.value;

            const response = await apiService.loadTableData(this.table, this.search, pageToLoad, this.sorting);

            if(response !== null) {
                this.tableData = response.data;
                this.pagination = {
                    showAll: false,
                    total: response.total,
                    perPage: response.per_page,
                    value: response.current_page,
                };
            }
            
            this.spinners.table = false;
        },
        searchData() {
            if(this.pagination.showAll) {
                this.showAll();
            } else {
                this.loadTableData(1);
            }
        },
        createEntry() {
            this.editEntry = {
                entry: new Localized({
                    name: '',
                    short_code: '',
                }),
                showErrors: false,
                showModal: true,
            };
        },
        async saveEntry(entry) {
            if(!entry.name || !entry.short_code || entry.short_code.length !== 2) {
                this.editEntry.showErrors = true;
            } else {
                entry.short_code = entry.short_code.toUpperCase();

                let response;

                if(typeof entry.id === 'undefined') {
                    response = await apiService.createEntry(this.table, entry);
                } else {
                    response = await apiService.updateEntry(this.table, entry.id, entry);
                }
                
                if(response === null) {
                    this.$notify({
                        type: 'danger',
                        title: 'Eintrag konnte nicht gespeichert werden!'
                    });
                } else {
                    this.editEntry.showModal = false;

                    this.$notify({
                        type: 'success',
                        title: 'Eintrag erfolgreich gespeichert!'
                    });
                }

                this.reload();
            }
        },
        editSelectedEntry(entry) {
            this.editEntry = {
                entry: new Localized({...entry}),
                showErrors: false,
                showModal: true,
            };
        },
    },
    created() {
        this.loadTableData();
    },
}
</script>
<style>
</style>
